import React from 'react';
import { ShieldCheck, Heart, Users } from 'lucide-react';

/**
 * TrustedPartners — compact credibility strip crediting Samaritans + Hey Sam
 * + co-design partners. Drop it on homepage, safety page, parent onboarding,
 * enterprise pitch, crisis handover screens.
 *
 * Variants:
 *   • "inline"   – one horizontal row (landing hero footer)
 *   • "stacked"  – vertical with expanded copy (safety page)
 *   • "compact"  – minimal (crisis screens / modals)
 */
const TrustedPartners = ({ variant = 'inline', className = '' }) => {
  const partners = [
    { name: 'Samaritans', region: 'UK', icon: <Heart size={14} /> },
    { name: 'Hey Sam',    region: 'US', icon: <ShieldCheck size={14} /> },
    { name: 'Crisis Text Line', region: 'Global', icon: <Users size={14} /> },
  ];

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-2 text-xs text-gray-500 ${className}`} data-testid="trusted-partners-compact">
        <span>In partnership with</span>
        <span className="font-semibold" style={{ color: '#F5831F' }}>Samaritans</span>
        <span>·</span>
        <span className="font-semibold" style={{ color: '#F5831F' }}>Hey Sam</span>
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div className={`bg-white rounded-xl border p-5 ${className}`} data-testid="trusted-partners-stacked" style={{ borderColor: '#E0E0E0' }}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B6B6B' }}>Crisis support partners</p>
        <div className="space-y-3">
          {partners.map((p) => (
            <div key={p.name} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(245,131,31,0.1)', color: '#F5831F' }}>
                {p.icon}
              </div>
              <div>
                <div className="text-sm font-semibold" style={{ color: '#1A1209' }}>{p.name}</div>
                <div className="text-xs text-gray-500">{p.region} region</div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
          When AvA detects a crisis signal, HiBFF hands off to a region-appropriate, trained human helpline —
          not a chatbot. Your child&apos;s safety is never automated.
        </p>
      </div>
    );
  }

  // Default inline
  return (
    <div className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 ${className}`} data-testid="trusted-partners-inline">
      <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#6B6B6B' }}>Crisis handover partners</span>
      {partners.map((p) => (
        <div key={p.name} className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#1A1209' }}>
          <span style={{ color: '#F5831F' }}>{p.icon}</span>
          {p.name}
          <span className="text-gray-400 font-normal">({p.region})</span>
        </div>
      ))}
    </div>
  );
};

export default TrustedPartners;
